import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { IMessagesTypeEnum } from "@ryde/controls";
import { useYupValidationResolver } from "@ryde/redux";
import { errors, Log, RecaptchaEnterpriseActions } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createActiveStepError,
  createMessage,
  HerzliyaLoginStepFormFieldsEnum,
  IVerifyCodeLandingModel,
  LANDING_VALID_OTP_REGEX,
  landingMessagesAtom,
  LandingStepEnum,
  LandingStepErrorsEnum,
  useLandingActions,
  useLandingApiAuth,
  useLandingOtpErrors,
  useLandingRecaptchaToken,
  VerifyCodeStepFormFieldsEnum,
} from "@ryde/utils-landings";

import { yupVerifyCodeLandingHerzliyaValidationSchema } from "./parts/verify-code.form.validation";

export const useStepVerifyCodeHerzliya = () => {
  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const [searchParams] = useSearchParams();
  const { goToStep, setActiveStepErrors } = useLandingActions();
  const { loginPublicOtp, requestPublicOtp } = useLandingApiAuth();
  const { handleErrors } = useLandingOtpErrors();
  const { getRecaptchaToken } = useLandingRecaptchaToken();
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { t } = useTranslation();

  const resolver = useYupValidationResolver(
    yupVerifyCodeLandingHerzliyaValidationSchema,
    "landing",
  );

  const methods = useForm<IVerifyCodeLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      verificationCode: "",
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const firstName = searchParams.get(HerzliyaLoginStepFormFieldsEnum.FirstName);
  const lastName = searchParams.get(HerzliyaLoginStepFormFieldsEnum.lastName);
  const phone = searchParams.get(HerzliyaLoginStepFormFieldsEnum.Phone);
  const verificationCodeWatchValue = watch(
    VerifyCodeStepFormFieldsEnum.VerificationCode,
  );

  const isContinueCanBeApply = useMemo(() => {
    const isValidOtp = LANDING_VALID_OTP_REGEX.test(verificationCodeWatchValue);
    return isValid && isValidOtp && phone && firstName && lastName;
  }, [firstName, isValid, lastName, phone, verificationCodeWatchValue]);

  const handleCodeVerification = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const verificationCode = getValues(
      VerifyCodeStepFormFieldsEnum.VerificationCode,
    );

    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.LOGIN,
    );

    if (verificationCode && phone && firstName && lastName) {
      setApiLoading(true);

      try {
        setServerErrors(createMessage([]));

        const { success, error, data } =
          (await loginPublicOtp({
            verificationCode,
            phone,
            firstName,
            lastName,
            recaptchaToken,
          })) ?? {};

        if (success && !error) {
          goToStep(LandingStepEnum.FinishedStep);
          setServerErrors(createMessage([]));
        } else if (error) {
          handleErrors({
            error,
            defaultAction(e) {
              setServerErrors(createMessage([t("errors.errorOtpIncorrect")]));
            },
          });
        }
      } catch (error) {
        Log.warn("Error while verifying code", error);
        setActiveStepErrors(
          createActiveStepError(LandingStepErrorsEnum.GeneralError),
        );
        goToStep(LandingStepEnum.ErrorStep);
      } finally {
        setApiLoading(false);
      }
    }
  });

  const resendOtp = useEvent(async (): Promise<void> => {
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.REQUEST_OTP,
    );

    if (phone && firstName && lastName) {
      setApiLoading(true);

      try {
        const requestOtpResponse = await requestPublicOtp({
          recaptchaToken,
          phone,
        });

        const { success, error } = requestOtpResponse ?? {};

        if (success) {
          setServerErrors(
            createMessage(
              [t("landing.resendOtpSuccess")],
              IMessagesTypeEnum.SUCCESS,
            ),
          );
          clearErrors(VerifyCodeStepFormFieldsEnum.VerificationCode);
        } else {
          handleErrors({
            error,
            defaultAction(e) {
              setServerErrors(
                createMessage([errors.auth.errorWhileRequestOTP]),
              );
            },
          });
        }
      } catch (error) {
        Log.warn("Error while requesting OTP", error);
        setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
      } finally {
        setApiLoading(false);
      }
    }
  });

  return { methods, handleCodeVerification, resendOtp, isContinueCanBeApply };
};
